import { AnimatePresence } from 'framer-motion';
import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { CrossfadePanel } from '../motion/index';

type LoadingCrossfadeProps = {
  // Render prop, not a node: the page content reads loaded data, so it must only be
  // evaluated in the loaded branch (when isLoading is false). A plain children node
  // would be built eagerly and could touch not-yet-loaded data.
  children: () => ReactNode;
  className?: string;
  isLoading: boolean;
  skeleton: ReactNode;
};

// Crossfades between a loading skeleton and the page content (opacity only, so it does
// not create a transform context that would break fixed-position modals or reveal
// table row-divider seams).
export const LoadingCrossfade: FC<LoadingCrossfadeProps> = ({
  children,
  className,
  isLoading,
  skeleton,
}) => {
  // The page content sits in a flex row in HeaderLayout (next to the ActivityBar) and
  // its root previously filled that row. This wrapper takes its place, so it must fill
  // too (h-full w-full) — otherwise it shrinks to content width and percentage heights
  // inside (h-full / min-h-full) no longer resolve. overflow stays visible so the
  // scroll container still scrolls tall content. min-w-0 lets the panel shrink inside
  // that flex row like the page roots it replaced (which had overflow-hidden) — without
  // it, wide content (graphs/tables) sets an intrinsic min-width and pushes the
  // ActivityBar out of the viewport.
  const panelClassName = twMerge('h-full w-full min-w-0', className);

  return (
    <AnimatePresence mode="wait">
      {isLoading ? (
        <CrossfadePanel
          className={panelClassName}
          key="loading"
          panelKey="loading"
          translate={false}
        >
          {skeleton}
        </CrossfadePanel>
      ) : (
        <CrossfadePanel
          className={panelClassName}
          key="content"
          panelKey="content"
          translate={false}
        >
          {children()}
        </CrossfadePanel>
      )}
    </AnimatePresence>
  );
};
